import { useToast } from '@1trading/react/hooks/use-toast';
import type { ErrorHandler, MessageHandler } from '@sumsub/websdk';
import type { SnsWebSdkBaseConfig, WebSDKEvents } from '@sumsub/websdk/types/types';
import SumsubWebSdk from '@sumsub/websdk-react';
import type React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent } from '@/components/ui/page-card';
import { useTheme } from '@/context/theme';
import { sumsubSnsWebSdkBaseConfig } from '@/features/verification/config/sumsub-sns-web-sdk';
import { useProfileVerificationSumsubToken } from '@/hooks/queries/use-profile-verification-information-sumsub-token';
import { useUserAttributes } from '@/hooks/queries/use-user-attributes';

type VerificationSumsubProps = React.PropsWithChildren<{
  onChange: (data: WebSDKEvents['idCheck.onApplicantStatusChanged']) => void;
  sdkConfig?: Partial<Omit<SnsWebSdkBaseConfig, 'email' | 'lang' | 'theme'>>;
}>;

/*
 * The new Sumsub workflow spans multiple levels. Because Sumsub doesn't emit an SDK event for applicantWorkflowCompleted like they do in their
 * webhooks implementation we need a way to ignore completion of levels which aren't the final level.
 *
 * This is a list of levels which are intermediate levels, i.e. not a final level. If we receive an event for any of these levels we will ignore it.
 *
 * Note: A conversation is on-going with Sumsub about this workaround. If they decide to add an event for applicantWorkflowCompleted or
 * have a better solution we will update this.
 */
const INTERMEDIATE_LEVELS = ['Nationality_Select'];

const VerificationSumsub = ({ children, onChange, sdkConfig }: VerificationSumsubProps) => {
  const { isFetching: isFetchingAccessToken, data: accessToken } = useProfileVerificationSumsubToken();
  const { isLoading: isLoadingUserAttributes, data: userAttributes } = useUserAttributes();
  const { toast } = useToast();
  const { inferredTheme } = useTheme();
  const { i18n, t } = useTranslation();
  const isLoading = isFetchingAccessToken || isLoadingUserAttributes;

  const handleError: ErrorHandler = useCallback(() => {
    toast({
      description: t('fundamentals.defaultError.message'),
      variant: 'destructive',
    });
  }, [toast, t]);

  const handleMessage: MessageHandler = useCallback(
    (type, data) => {
      console.log('handleMessage', type, data);

      switch (type) {
        /*
         * idCheck.onApplicantStatusChanged is the only event the Sumsub SDK emits with level information included.
         * Ideally we would use onApplicantSubmitted but this doesn't include level information.
         *
         * Note: We're ignoring events with non-submitted statuses. For more information see:
         * https://docs.sumsub.com/reference/get-applicant-review-status#review-statuses
         */
        case 'idCheck.onApplicantStatusChanged': {
          const eventData = data as WebSDKEvents['idCheck.onApplicantStatusChanged'];

          if (INTERMEDIATE_LEVELS.includes(eventData.levelName) || eventData.reviewStatus === 'init') {
            return;
          }

          onChange(eventData);
          break;
        }
      }
    },
    [onChange],
  );

  if (isLoading) return <LoadingCard />;

  if (!accessToken) throw new Error('Unable to proceed with sumsub, no access');

  return (
    <PageCard size="lg" bordered fixedFooter>
      <PageCardContent className="pt-8">
        <SumsubWebSdk
          key={`${i18n.language}-${inferredTheme}-${accessToken}`}
          accessToken={accessToken}
          className="overflow-hidden rounded-lg"
          config={{
            ...sumsubSnsWebSdkBaseConfig,
            ...sdkConfig,
            email: userAttributes?.email,
            lang: i18n.language,
            theme: inferredTheme,
          }}
          // Unhandled
          expirationHandler={() => Promise.resolve(accessToken)}
          onError={handleError}
          onMessage={handleMessage}
        />
      </PageCardContent>
      {children}
    </PageCard>
  );
};
VerificationSumsub.displayName = 'VerificationSumsub';

export default VerificationSumsub;
